import Button from 'components/Form/Button';
import CustomSelect from 'components/Form/CustomSelect';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { MaritalStatus } from 'constants/marital-status';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import Male from '../../../assets/man.png';
import { GuardianTypeEnum } from '../types/user.enum.type';
import { RelationshipType } from 'constants/bank-account-type';
import { GuardianType } from '../types/on-boarding.type';
import { useCreateGuardian } from 'common/queries-and-mutations/guardian';
import { InfoModal } from 'components/Modal';
import { Children } from 'service/dto/children.dto';
import GuardianChildrenChecklist from './GuardianChildrenChecklist';

type Props = {
  type: GuardianTypeEnum;
  childrens: Children;
  selectedChildIds: number[];
  onSelectedChildIdsChange: (ids: number[]) => void;
  onClose: () => void;
  onComplete: () => void;
};

export default function AddAssignGuardianModal({
  type,
  childrens,
  selectedChildIds,
  onSelectedChildIdsChange,
  onClose,
  onComplete
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate, isSuccess, isError, isLoading } = useCreateGuardian();

  const formik = useFormik({
    initialValues: {
      guardians: [
        {
          guardianFirstName: '',
          guardianMiddleName: '',
          guardianLastName: '',
          guardianType: type,
          relationship: '',
          maritalStatus: '',
          address: '',
          emailAddress: '',
          phoneNumber: '',
          dateOfBirth: ''
        }
      ]
    },
    validationSchema: Yup.object({
      guardians: Yup.array().of(
        Yup.object().shape({
          guardianFirstName: Yup.string()
            .required('Guardian first name is required')
            .nullable(),
          // guardianMiddleName: Yup.string()
          //   .required('Guardian middle name is required')
          //   .nullable(),
          guardianLastName: Yup.string()
            .required('Guardian last name is required')
            .nullable(),
          relationship: Yup.string()
            .required('Relationship is required')
            .nullable(),
          maritalStatus: Yup.string()
            .required('Marital status is required')
            .nullable(),
          address: Yup.string().required('Home address is required').nullable(),
          emailAddress: Yup.string()
            .email('Invalid email address')
            .required('Email is required')
            .nullable(),
          phoneNumber: Yup.string()
            .required('Phone number is required')
            .nullable(),
          dateOfBirth: Yup.date()
            .required('Date of Birth is required')
            .nullable()
        })
      )
    }),
    onSubmit: (values, { setFieldTouched }) => {
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onGuardianSubmit(values);
    },
    enableReinitialize: true
  });

  const onGuardianSubmit = (formValue: GuardianType) => {
    setIsSubmitting(false);
    //Create the guardian and link it to the selected children in one request
    mutate({
      guardians: formValue.guardians.map(guardian => ({
        ...guardian,
        childrenIds: selectedChildIds
      }))
    });
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      //Keep the form busy until the parent remounts it for the next round
      setIsSubmitting(true);
      onComplete();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <InfoModal
      width="w-[55%]"
      className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
    >
      <div>
        <div className="flex flex-row items-center justify-end pl-6">
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <div className="flex flex-row items-center pl-4">
          <img src={Male} className="h-10 w-10" />
          <p className="text-lg font-bold text-[#353535]">
            {type === GuardianTypeEnum.PRIMARY
              ? 'Your child is under 18, please add a primary guardian.'
              : 'Add a substitute guardian (optional).'}
          </p>
        </div>
        <form
          autoComplete="false"
          className="mt-10 px-6"
          onSubmit={formik.handleSubmit}
        >
          <GuardianChildrenChecklist
            childrens={childrens}
            selectedIds={selectedChildIds}
            onChange={onSelectedChildIdsChange}
          />
          {formik.values.guardians.map((guardian, index) => (
            <div key={index} className="mb-1 mt-8">
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].guardianFirstName`}
                    placeholder=""
                    value={guardian.guardianFirstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Guardian First Name"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).guardianFirstName
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].guardianMiddleName`}
                    placeholder=""
                    value={guardian.guardianMiddleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Guardian Middle Name"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).guardianMiddleName
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].guardianLastName`}
                    placeholder=""
                    value={guardian.guardianLastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Guardian Last Name"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).guardianLastName
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <CustomSelect
                    options={RelationshipType}
                    selectedOption={{
                      value: guardian.relationship,
                      label: guardian.relationship
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(
                        `guardians[${index}].relationship`,
                        option?.value
                      );
                    }}
                    label="Relationship"
                    name="relationship"
                    isRequired
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).relationship
                          : ''
                        : ''
                    }
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <CustomSelect
                    options={MaritalStatus}
                    selectedOption={{
                      value: guardian.maritalStatus,
                      label: guardian.maritalStatus
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(
                        `guardians[${index}].maritalStatus`,
                        option?.value
                      );
                    }}
                    label="Marital Status"
                    name="maritalStatus"
                    isRequired
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).maritalStatus
                          : ''
                        : ''
                    }
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].address`}
                    placeholder=""
                    isRequired
                    value={guardian.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Home Address"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).address
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].emailAddress`}
                    placeholder=""
                    isRequired
                    value={guardian.emailAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Email Address"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).emailAddress
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].phoneNumber`}
                    placeholder=""
                    isRequired
                    value={guardian.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Phone Number"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).phoneNumber
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].dateOfBirth`}
                    type="date"
                    isRequired
                    placeholder=""
                    value={guardian.dateOfBirth}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Date of Birth"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).dateOfBirth
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            </div>
          ))}
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="md"
              className={`col-start-2 col-end-2 my-5 w-[10rem] rounded p-[0.2rem]`}
              label="Add Guardian"
              disabled={!formik.isValid || !selectedChildIds.length}
              loading={isSubmitting || isLoading}
              type="submit"
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
